/**
 * C.13c — `.flow` JSON serializer.
 *
 * Round-trip contract:
 *   - `deserializeFlow(raw)` parses the JSON and returns `{ doc, dirty: false }`.
 *   - `serializeFlow(doc)` re-emits canonical JSON. If `doc.raw` matches
 *     a re-serialized canonical form of itself (no edits), we return
 *     `doc.raw` verbatim so unchanged files don't drift.
 *
 * The pinned schema version is 1. The serializer never throws — bad
 * input becomes an empty flow with `error` set so the UI can surface it.
 */

export const FLOW_SCHEMA_VERSION = 1;

export type FlowNodeKind =
  | 'CaseNode'
  | 'StatuteNode'
  | 'PartyNode'
  | 'EvidenceNode'
  | 'NoteNode'
  | 'TextNode';

export interface FlowNode {
  id: string;
  type: FlowNodeKind;
  position: { x: number; y: number };
  data: Record<string, unknown>;
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  type?: string;
  data?: Record<string, unknown>;
}

export interface FlowDoc {
  version: number;
  name: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
  /** The original bytes, retained so unedited flows round-trip byte-for-byte. */
  raw: string;
  /** Non-null after deserialize if the input had problems. */
  error?: string;
}

export function deserializeFlow(raw: string, fallbackName = 'untitled'): FlowDoc {
  if (!raw.trim()) {
    return makeEmpty(raw, fallbackName);
  }
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch (e) {
    return { ...makeEmpty(raw, fallbackName), error: (e as Error).message };
  }
  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    return { ...makeEmpty(raw, fallbackName), error: 'flow root must be an object' };
  }
  const obj = json as Record<string, unknown>;
  return {
    version: typeof obj['version'] === 'number' ? (obj['version'] as number) : FLOW_SCHEMA_VERSION,
    name: typeof obj['name'] === 'string' ? (obj['name'] as string) : fallbackName,
    nodes: Array.isArray(obj['nodes']) ? (obj['nodes'] as unknown[]).map(coerceNode).filter(isPresent) : [],
    edges: Array.isArray(obj['edges']) ? (obj['edges'] as unknown[]).map(coerceEdge).filter(isPresent) : [],
    raw,
  };
}

export function serializeFlow(doc: FlowDoc): string {
  const canonical = canonicalize(doc);
  // If the canonical form of the in-memory doc matches the canonical
  // form of the parsed-original-raw, return raw verbatim. This preserves
  // formatting (spaces, key order, trailing newlines) for unedited files.
  if (doc.raw) {
    try {
      const reparsedRaw = JSON.parse(doc.raw) as unknown;
      const canonicalRaw = JSON.stringify(stableSort(reparsedRaw));
      const canonicalNow = JSON.stringify(stableSort(canonical));
      if (canonicalRaw === canonicalNow) {
        return doc.raw;
      }
    } catch {
      // fall through — raw was unparseable, emit fresh
    }
  }
  return JSON.stringify(canonical, null, 2);
}

function canonicalize(doc: FlowDoc): {
  version: number;
  name: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
} {
  return {
    version: doc.version || FLOW_SCHEMA_VERSION,
    name: doc.name,
    nodes: doc.nodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: { x: n.position.x, y: n.position.y },
      data: n.data ?? {},
    })),
    edges: doc.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      ...(e.label !== undefined ? { label: e.label } : {}),
      ...(e.type !== undefined ? { type: e.type } : {}),
      ...(e.data !== undefined ? { data: e.data } : {}),
    })),
  };
}

function stableSort(input: unknown): unknown {
  if (Array.isArray(input)) return input.map(stableSort);
  if (input && typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    return Object.keys(obj)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = stableSort(obj[k]);
        return acc;
      }, {});
  }
  return input;
}

function makeEmpty(raw: string, fallbackName: string): FlowDoc {
  return {
    version: FLOW_SCHEMA_VERSION,
    name: fallbackName,
    nodes: [],
    edges: [],
    raw,
  };
}

const ALLOWED_NODE_KINDS = new Set<FlowNodeKind>([
  'CaseNode',
  'StatuteNode',
  'PartyNode',
  'EvidenceNode',
  'NoteNode',
  'TextNode',
]);

function coerceNode(raw: unknown): FlowNode | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r['id'] !== 'string' || typeof r['type'] !== 'string') return null;
  const kind = r['type'] as FlowNodeKind;
  if (!ALLOWED_NODE_KINDS.has(kind)) return null;
  const pos = r['position'] as Record<string, unknown> | undefined;
  const x = typeof pos?.['x'] === 'number' ? (pos['x'] as number) : 0;
  const y = typeof pos?.['y'] === 'number' ? (pos['y'] as number) : 0;
  return {
    id: r['id'] as string,
    type: kind,
    position: { x, y },
    data: (r['data'] as Record<string, unknown>) ?? {},
  };
}

function coerceEdge(raw: unknown): FlowEdge | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (
    typeof r['id'] !== 'string' ||
    typeof r['source'] !== 'string' ||
    typeof r['target'] !== 'string'
  ) {
    return null;
  }
  return {
    id: r['id'] as string,
    source: r['source'] as string,
    target: r['target'] as string,
    ...(typeof r['label'] === 'string' ? { label: r['label'] as string } : {}),
    ...(typeof r['type'] === 'string' ? { type: r['type'] as string } : {}),
    ...(r['data'] && typeof r['data'] === 'object'
      ? { data: r['data'] as Record<string, unknown> }
      : {}),
  };
}

function isPresent<T>(x: T | null): x is T {
  return x !== null;
}
